<template>
  <v-dialog v-model="visible" max-width="600px" persistent>
    <v-card class="mb-0">
      <v-card-title class="headline">
        <v-icon left>{{ iconoDeporte }}</v-icon>
        {{ tituloModal }}
      </v-card-title>

      <v-card-subtitle v-if="partido">
        <v-row class="mt-6">
          <v-col cols="5">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  color="primary"
                  class="nombre-equipo"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ partido.local?.nombre }}
                </v-chip>
              </template>
              <span>{{ partido.local?.nombre }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="2">
            <strong>VS</strong>
          </v-col>
          <v-col cols="5">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  color="secondary"
                  class="nombre-equipo"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ partido.visitante?.nombre }}
                </v-chip>
              </template>
              <span>{{ partido.visitante?.nombre }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-subtitle>

      <v-card-text>
        <!-- FÚTBOL: Solo un partido con goles -->
        <v-form v-if="esFutbol" @submit.prevent ref="form">
          <v-row class="mb-3">
            <v-col cols="12">
              <h4 class="subtitle-1">Resultado del Partido</h4>
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="resultadoFutbol.golesLocal"
                type="number"
                label="Goles Local"
                min="0"
                outlined
                dense
                prepend-inner-icon="mdi-soccer"
              ></v-text-field>
            </v-col>
            <v-col cols="2" class="text-center pt-6">
              <strong>-</strong>
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="resultadoFutbol.golesVisitante"
                type="number"
                label="Goles Visitante"
                min="0"
                outlined
                dense
                prepend-inner-icon="mdi-soccer"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-alert v-if="!formValidoFutbol" type="warning" dense>
            Debe ingresar los goles de ambos equipos
          </v-alert>
        </v-form>

        <!-- TENIS/PADEL: Sets con games -->
        <v-form v-else @submit.prevent ref="form">
          <v-row v-for="(set, index) in sets" :key="index" class="mb-3">
            <v-col cols="12">
              <h4 class="subtitle-1">Set {{ index + 1 }}</h4>
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="set.gamesLocal"
                type="number"
                label="Games Local"
                min="0"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="2" class="text-center pt-6">
              <strong>-</strong>
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="set.gamesVisitante"
                type="number"
                label="Games Visitante"
                min="0"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-alert v-if="sets.length < 2" type="warning" dense>
            Se necesitan mínimo 2 sets completos
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cerrar" color="grey" dark>Cancelar</v-btn>
        <v-btn @click="guardar" color="success" :disabled="!formValido">
          Guardar Resultado
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { showAlert } from "@/utils/toast";

export default defineComponent({
  name: "CargarResultado",

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    partido: {
      type: Object,
      default: null,
    },
    proxy: {
      type: Object,
      default: null,
    },
    deporte: {
      type: String,
      default: "Padel", // 'Tenis', 'Padel', 'Futbol'
    },
  },

  emits: ["close", "save"],

  setup(props, { emit }) {
    // Estado para tenis/padel
    const sets = ref([
      { gamesLocal: null, gamesVisitante: null },
      { gamesLocal: null, gamesVisitante: null },
      { gamesLocal: null, gamesVisitante: null },
    ]);

    // Estado para fútbol
    const resultadoFutbol = ref<{
      golesLocal: number | null;
      golesVisitante: number | null;
    }>({
      golesLocal: null,
      golesVisitante: null,
    });

    // Detectar si es fútbol
    const esFutbol = computed(() => {
      return props.deporte === "Futbol";
    });

    // Ícono según deporte
    const iconoDeporte = computed(() => {
      if (esFutbol.value) return "mdi-soccer";
      return "mdi-scoreboard";
    });

    // Título según deporte
    const tituloModal = computed(() => {
      if (esFutbol.value) return "Resultado del Partido";

      const esPartidoDeZona = props.partido && !props.partido.ronda;
      return esPartidoDeZona ? "Resultado de Zona" : "Resultado de Eliminación";
    });

    // Validación para fútbol
    const formValidoFutbol = computed(() => {
      return (
        (Number.isInteger(resultadoFutbol.value.golesLocal) &&
          Number.isInteger(resultadoFutbol.value.golesVisitante) &&
          resultadoFutbol.value?.golesLocal) ||
        (0 >= 0 && resultadoFutbol.value?.golesVisitante) ||
        0 >= 0
      );
    });

    // Validación para tenis/padel
    const formValidoTenisPadel = computed(() => {
      const setsCompletos = sets.value.filter(
        (s) =>
          Number.isInteger(s.gamesLocal) && Number.isInteger(s.gamesVisitante),
      );
      return setsCompletos.length >= 2;
    });

    // Validación general
    const formValido = computed(() => {
      if (esFutbol.value) {
        return formValidoFutbol.value;
      }
      return formValidoTenisPadel.value;
    });

    // Sincronizar datos cuando cambia el partido
    watch(
      () => props.partido,
      (nuevoPartido) => {
        if (!nuevoPartido) return;

        if (esFutbol.value) {
          // Cargar resultado de fútbol existente
          if (nuevoPartido.resultado) {
            const partes = nuevoPartido.resultado.split("-");
            if (partes.length === 2) {
              resultadoFutbol.value.golesLocal = Number.isNaN(Number(partes[0]))
                ? null
                : Number(partes[0]);

              resultadoFutbol.value.golesVisitante = Number.isNaN(
                Number(partes[1]),
              )
                ? null
                : Number(partes[1]);
            }
          } else {
            resultadoFutbol.value.golesLocal = null;
            resultadoFutbol.value.golesVisitante = null;
          }
        } else {
          // Cargar sets existentes (tenis/padel)
          if (nuevoPartido.sets && Array.isArray(nuevoPartido.sets)) {
            sets.value = nuevoPartido.sets.map((s: any) => ({
              gamesLocal: s.gamesLocal,
              gamesVisitante: s.gamesVisitante,
            }));

            while (sets.value.length < 3) {
              sets.value.push({ gamesLocal: null, gamesVisitante: null });
            }
          } else {
            sets.value = [
              { gamesLocal: null, gamesVisitante: null },
              { gamesLocal: null, gamesVisitante: null },
              { gamesLocal: null, gamesVisitante: null },
            ];
          }
        }
      },
      { immediate: true },
    );

    const cerrar = () => {
      emit("close");
    };

    const guardar = () => {
      if (!formValido.value) {
        const mensaje = esFutbol.value
          ? "Por favor ingrese los goles de ambos equipos."
          : "Por favor complete al menos 2 sets con valores válidos.";

        showAlert({
          title: mensaje,
          timer: 4000,
          vm: props.proxy,
          icon: "warning",
        });
        return;
      }

      if (esFutbol.value) {
        // Guardar resultado de fútbol
        // Convertimos a formato de "sets" para mantener compatibilidad
        const setsFutbol = [
          {
            gamesLocal: resultadoFutbol.value.golesLocal,
            gamesVisitante: resultadoFutbol.value.golesVisitante,
          },
        ];
        emit("save", setsFutbol);
      } else {
        // Guardar sets de tenis/padel
        if (
          sets.value.some(
            (set) =>
              (set.gamesLocal && set.gamesLocal < 0) ||
              (set.gamesVisitante && set.gamesVisitante < 0),
          )
        ) {
          showAlert({
            title: "Los games no pueden ser números negativos.",
            timer: 4000,
            vm: props.proxy,
            icon: "warning",
          });
          return;
        }

        const setsParaGuardar = sets.value.filter(
          (s) =>
            Number.isInteger(s.gamesLocal) &&
            Number.isInteger(s.gamesVisitante),
        );

        emit("save", setsParaGuardar);
      }
    };

    return {
      sets,
      resultadoFutbol,
      esFutbol,
      iconoDeporte,
      tituloModal,
      formValidoFutbol,
      formValidoTenisPadel,
      formValido,
      cerrar,
      guardar,
    };
  },
});
</script>

<style scoped>
.nombre-equipo {
  span {
    margin-top: 12px;
    display: inline-block !important;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
